import React from 'react';
import PropTypes from 'prop-types';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Input,
	Select,
	SelectItem,
	Button,
	Checkbox,
	useDisclosure,
	addToast
} from '@heroui/react';
import { Trash2 } from 'lucide-react';

// Pay status options (from php/models/Resources.php)
const PAY_STATUSES = [
	{ value: '', name: 'Not Applicable' },
	{ value: 'perhour', name: 'By Meter' },
	{ value: 'peruse', name: 'Per Use' },
	{ value: 'perblock', name: 'Scheduled Time' }
];

function EditResourceModal({
	isOpen,
	onClose,
	resource = null,
	locations = [],
	payStatuses = [],
	onSave,
	onDelete,
	saving = false
}) {
	const deleteConfirmation = useDisclosure();
	const [formData, setFormData] = React.useState({
		name: '',
		locationID: '',
		rate: '',
		payStatus: '',
		taxable: false
	});
	const [errors, setErrors] = React.useState({});
	const [isDeleting, setIsDeleting] = React.useState(false);

	// Reset form when resource changes
	React.useEffect(() => {
		if (resource) {
			// Handle both SSR format (locationID) and API format (location object)
			const locationId =
				resource.locationID ||
				(typeof resource.location === 'object' ? resource.location?.id : resource.location) ||
				'';

			setFormData({
				name: resource.name || '',
				locationID: String(locationId),
				rate: resource.rate || '',
				payStatus: resource.payStatus || '',
				taxable: Boolean(resource.taxable)
			});
			setErrors({});
		}
	}, [resource]);

	// Reset form when modal closes
	React.useEffect(() => {
		if (!isOpen) {
			setErrors({});
		}
	}, [isOpen]);

	// Validate form fields
	const validate = () => {
		const newErrors = {};

		// Name is required
		if (!formData.name || formData.name.trim().length === 0) {
			newErrors.name = 'Resource name is required';
		}

		// Location is required
		if (!formData.locationID) {
			newErrors.locationID = 'Location is required';
		}

		// Rate must be positive if filled
		if (formData.rate !== '' && formData.rate !== null && formData.rate !== undefined) {
			const rateNum = parseFloat(formData.rate);
			if (isNaN(rateNum) || rateNum < 0) {
				newErrors.rate = 'Rate must be a positive number';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle form field changes
	const handleInputChange = (field, value) => {
		setFormData(prev => {
			const newData = { ...prev, [field]: value };
			// Clear rate if charge type is set to "Not Applicable"
			if (field === 'payStatus' && !value) {
				newData.rate = '';
			}
			return newData;
		});
		// Clear error for this field when user starts typing
		if (errors[field]) {
			setErrors(prev => ({ ...prev, [field]: null }));
		}
	};

	// Handle name field blur for validation
	const handleNameBlur = () => {
		if (!formData.name || formData.name.trim().length === 0) {
			setErrors(prev => ({ ...prev, name: 'Resource name is required' }));
		}
	};

	// Handle form submission
	const handleSubmit = async () => {
		if (!validate()) return;
		if (!resource) return;

		// Build the data object for API
		const dataToSave = {
			name: formData.name.trim(),
			locationId: parseInt(formData.locationID, 10),
			taxable: formData.taxable
		};

		// Include rate and payStatus if charge type is selected
		if (formData.payStatus) {
			dataToSave.payStatus = formData.payStatus;
			dataToSave.rate = formData.rate?.toString().trim() || null;
		} else {
			dataToSave.rate = null;
			dataToSave.payStatus = null;
		}

		try {
			await onSave(resource.id, dataToSave);
			addToast({
				title: 'Success',
				description: 'Resource updated successfully',
				color: 'success'
			});
			onClose();
		} catch (err) {
			const errorMessage =
				err.response?.data?.message || err.message || 'Failed to update resource';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		}
	};

	// Handle delete action
	const handleDelete = async () => {
		if (!resource) return;

		setIsDeleting(true);
		try {
			await onDelete(resource.id);
			addToast({
				title: 'Success',
				description: 'Resource deleted successfully',
				color: 'success'
			});
			deleteConfirmation.onClose();
			onClose();
		} catch (err) {
			const errorMessage =
				err.response?.data?.message || err.message || 'Failed to delete resource';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setIsDeleting(false);
		}
	};

	// Handle modal close - reset state
	const handleClose = () => {
		setErrors({});
		onClose();
	};

	// Location options for select
	const locationOptions = React.useMemo(() => {
		if (!locations) return [];
		return locations.map(loc => ({
			id: String(loc.id),
			name: loc.name
		}));
	}, [locations]);

	// Determine if rate field should be shown (when charge type is selected)
	const showRate = formData.payStatus !== '' && formData.payStatus !== null;

	return (
		<>
			{/* Edit Resource Modal */}
			<Modal scrollBehavior="inside" isOpen={isOpen} onClose={handleClose} size="lg">
				<ModalContent>
					<ModalHeader className="flex flex-col gap-1">Edit Resource</ModalHeader>
					<ModalBody>
						<div className="space-y-4">
							{/* Name Input */}
							<Input
								label="Name"
								placeholder="Enter resource name"
								variant="bordered"
								value={formData.name}
								onValueChange={value => handleInputChange('name', value)}
								onBlur={handleNameBlur}
								isInvalid={!!errors.name}
								errorMessage={errors.name}
								isRequired
							/>

							{/* Location Select */}
							<Select
								label="Location"
								placeholder="Select location"
								variant="bordered"
								aria-label="Select location"
								selectedKeys={formData.locationID ? [formData.locationID] : []}
								onSelectionChange={keys => {
									const selected = Array.from(keys)[0];
									handleInputChange('locationID', selected || '');
								}}
								isInvalid={!!errors.locationID}
								errorMessage={errors.locationID}
								isRequired
							>
								{locationOptions.map(location => (
									<SelectItem key={location.id}>{location.name}</SelectItem>
								))}
							</Select>

							{/* Charge Type Select - always shown */}
							<Select
								label="Charge Type"
								placeholder="Select charge type"
								variant="bordered"
								aria-label="Charge Type"
								selectedKeys={formData.payStatus ? [formData.payStatus] : ['none']}
								onSelectionChange={keys => {
									const selected = Array.from(keys)[0];
									handleInputChange('payStatus', selected === 'none' ? '' : selected);
								}}
							>
								<SelectItem key="none">Not Applicable</SelectItem>
								{PAY_STATUSES.filter(s => s.value).map(status => (
									<SelectItem key={status.value}>{status.name}</SelectItem>
								))}
							</Select>

							{/* Rate Input - only shown if charge type is selected */}
							{showRate && (
								<Input
									label="Rate"
									placeholder="Enter rate"
									variant="bordered"
									type="number"
									min="0"
									step="0.01"
									startContent="$"
									value={formData.rate}
									onValueChange={value => handleInputChange('rate', value)}
									isInvalid={!!errors.rate}
									errorMessage={errors.rate}
								/>
							)}

							{/* Taxable Checkbox */}
							<Checkbox
								isSelected={formData.taxable}
								onValueChange={value => handleInputChange('taxable', value)}
							>
								Taxable
							</Checkbox>
						</div>
					</ModalBody>
					<ModalFooter className="flex justify-between">
						<Button
							color="danger"
							variant="light"
							startContent={<Trash2 className="w-4 h-4" />}
							onPress={deleteConfirmation.onOpen}
							isDisabled={saving || isDeleting}
						>
							Delete
						</Button>
						<div className="flex gap-2">
							<Button variant="light" onPress={handleClose} isDisabled={saving}>
								Cancel
							</Button>
							<Button
								color="primary"
								onPress={handleSubmit}
								isLoading={saving}
								isDisabled={isDeleting}
							>
								Save
							</Button>
						</div>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Delete Confirmation Modal */}
			<Modal scrollBehavior="inside" isOpen={deleteConfirmation.isOpen} onClose={deleteConfirmation.onClose} size="sm">
				<ModalContent>
					<ModalHeader className="flex flex-col gap-1">Confirm Delete</ModalHeader>
					<ModalBody>
						<p>Are you sure you want to delete the resource "{resource?.name}"?</p>
						<p className="text-default-500 text-sm">This action cannot be undone.</p>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={deleteConfirmation.onClose} isDisabled={isDeleting}>
							Cancel
						</Button>
						<Button color="danger" onPress={handleDelete} isLoading={isDeleting}>
							Delete
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}

EditResourceModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	resource: PropTypes.object,
	locations: PropTypes.array,
	payStatuses: PropTypes.array,
	onSave: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	saving: PropTypes.bool
};

export default EditResourceModal;
